/**
 * System Prompt Architect
 * Guided wizard for building LLM system prompts with security hardening
 */
import {
  assemblePrompt,
  applyTemplate,
  calculateHardeningScore,
  createDefaultState,
  getScoreLabel,
  stepHasContent,
  STEPS,
  type ArchitectState,
} from '../../lib/labs/system-prompt-architect';

// ── State ──────────────────────────────────────────────────────────

let state: ArchitectState = createDefaultState();
let exampleCounter = 0;

// ── DOM refs ───────────────────────────────────────────────────────

let stepNavBtns: NodeListOf<HTMLButtonElement>;
let stepPills: NodeListOf<HTMLButtonElement>;
let stepPanels: NodeListOf<HTMLElement>;
let templateBtns: NodeListOf<HTMLButtonElement>;
let defenseBtns: NodeListOf<HTMLButtonElement>;

let btnPrev: HTMLButtonElement;
let btnNext: HTMLButtonElement;
let btnAddExample: HTMLButtonElement;
let btnCopy: HTMLButtonElement;
let btnDownload: HTMLButtonElement;
let btnCopyMobile: HTMLButtonElement;
let btnDownloadMobile: HTMLButtonElement;
let btnMobilePreview: HTMLButtonElement;
let btnClosePreview: HTMLButtonElement;

let examplesList: HTMLElement;
let previewText: HTMLElement;
let mobilePreviewText: HTMLElement;
let mobilePreviewOverlay: HTMLElement;

let previewScore: HTMLElement;
let previewScoreLabel: HTMLElement;
let previewScoreBar: HTMLElement;
let mobilePreviewScore: HTMLElement;
let mobilePreviewScoreLabel: HTMLElement;
let mobilePreviewScoreBar: HTMLElement;

let hardeningScoreLabel: HTMLElement;
let hardeningScoreBar: HTMLElement;

// ── Field mapping ──────────────────────────────────────────────────

interface FieldMapping {
  id: string;
  step: keyof Omit<ArchitectState, 'currentStep' | 'examples' | 'hardening'>;
  field: string;
  type: 'input' | 'textarea';
}

const FIELD_MAPPINGS: FieldMapping[] = [
  { id: 'identity-roleName', step: 'identity', field: 'roleName', type: 'input' },
  {
    id: 'identity-expertiseDomain',
    step: 'identity',
    field: 'expertiseDomain',
    type: 'input',
  },
  {
    id: 'identity-personalityTraits',
    step: 'identity',
    field: 'personalityTraits',
    type: 'input',
  },
  {
    id: 'identity-customIdentity',
    step: 'identity',
    field: 'customIdentity',
    type: 'textarea',
  },
  {
    id: 'knowledge-knowledgeAreas',
    step: 'knowledge',
    field: 'knowledgeAreas',
    type: 'textarea',
  },
  {
    id: 'knowledge-refusalTopics',
    step: 'knowledge',
    field: 'refusalTopics',
    type: 'textarea',
  },
  {
    id: 'knowledge-citationBehavior',
    step: 'knowledge',
    field: 'citationBehavior',
    type: 'input',
  },
  {
    id: 'knowledge-customKnowledge',
    step: 'knowledge',
    field: 'customKnowledge',
    type: 'textarea',
  },
  { id: 'behavior-tone', step: 'behavior', field: 'tone', type: 'input' },
  { id: 'behavior-verbosity', step: 'behavior', field: 'verbosity', type: 'input' },
  {
    id: 'behavior-formatPreference',
    step: 'behavior',
    field: 'formatPreference',
    type: 'input',
  },
  { id: 'behavior-language', step: 'behavior', field: 'language', type: 'input' },
  {
    id: 'behavior-customRules',
    step: 'behavior',
    field: 'customRules',
    type: 'textarea',
  },
  {
    id: 'task-primaryCapability',
    step: 'task',
    field: 'primaryCapability',
    type: 'input',
  },
  {
    id: 'task-supportedOperations',
    step: 'task',
    field: 'supportedOperations',
    type: 'textarea',
  },
  {
    id: 'task-workflowDescription',
    step: 'task',
    field: 'workflowDescription',
    type: 'textarea',
  },
  { id: 'output-responseFormat', step: 'output', field: 'responseFormat', type: 'input' },
  { id: 'output-maxLength', step: 'output', field: 'maxLength', type: 'input' },
  { id: 'output-requiredFields', step: 'output', field: 'requiredFields', type: 'input' },
  {
    id: 'output-structureTemplate',
    step: 'output',
    field: 'structureTemplate',
    type: 'textarea',
  },
];

// ── Init ───────────────────────────────────────────────────────────

function init() {
  stepNavBtns = document.querySelectorAll('.step-nav');
  stepPills = document.querySelectorAll('.step-pill');
  stepPanels = document.querySelectorAll('.step-panel');
  templateBtns = document.querySelectorAll('.template-btn');
  defenseBtns = document.querySelectorAll('.defense-toggle');

  btnPrev = document.getElementById('btn-prev') as HTMLButtonElement;
  btnNext = document.getElementById('btn-next') as HTMLButtonElement;
  btnAddExample = document.getElementById('btn-add-example') as HTMLButtonElement;
  btnCopy = document.getElementById('btn-copy') as HTMLButtonElement;
  btnDownload = document.getElementById('btn-download') as HTMLButtonElement;
  btnCopyMobile = document.getElementById('btn-copy-mobile') as HTMLButtonElement;
  btnDownloadMobile = document.getElementById('btn-download-mobile') as HTMLButtonElement;
  btnMobilePreview = document.getElementById('btn-mobile-preview') as HTMLButtonElement;
  btnClosePreview = document.getElementById('btn-close-preview') as HTMLButtonElement;

  examplesList = document.getElementById('examples-list') as HTMLElement;
  previewText = document.getElementById('preview-text') as HTMLElement;
  mobilePreviewText = document.getElementById('mobile-preview-text') as HTMLElement;
  mobilePreviewOverlay = document.getElementById('mobile-preview-overlay') as HTMLElement;

  previewScore = document.getElementById('preview-score') as HTMLElement;
  previewScoreLabel = document.getElementById('preview-score-label') as HTMLElement;
  previewScoreBar = document.getElementById('preview-score-bar') as HTMLElement;
  mobilePreviewScore = document.getElementById('mobile-preview-score') as HTMLElement;
  mobilePreviewScoreLabel = document.getElementById(
    'mobile-preview-score-label'
  ) as HTMLElement;
  mobilePreviewScoreBar = document.getElementById(
    'mobile-preview-score-bar'
  ) as HTMLElement;

  hardeningScoreLabel = document.getElementById('hardening-score-label') as HTMLElement;
  hardeningScoreBar = document.getElementById('hardening-score-bar') as HTMLElement;

  setupEventListeners();
  updatePreview();
}

function setupEventListeners() {
  // Step navigation (desktop sidebar)
  stepNavBtns.forEach((btn) => {
    btn.addEventListener('click', () => {
      const step = parseInt(btn.dataset.goto ?? '0', 10);
      goToStep(step);
    });
  });

  // Step navigation (mobile pills)
  stepPills.forEach((btn) => {
    btn.addEventListener('click', () => {
      const step = parseInt(btn.dataset.goto ?? '0', 10);
      goToStep(step);
    });
  });

  // Prev / Next
  btnPrev.addEventListener('click', () => navigateStep(-1));
  btnNext.addEventListener('click', () => navigateStep(1));

  // Templates
  templateBtns.forEach((btn) => {
    btn.addEventListener('click', () => {
      const templateId = btn.dataset.template;
      if (templateId) {
        state = applyTemplate(state, templateId);
        populateAllInputs();
        updateDefenseToggles();
        updatePreview();
      }
    });
  });

  // Defense toggles
  defenseBtns.forEach((btn) => {
    btn.addEventListener('click', () => {
      const defenseId = btn.dataset.defenseId;
      if (defenseId) toggleDefense(defenseId);
    });
  });

  // Examples
  btnAddExample.addEventListener('click', addExample);

  // Copy / Download
  btnCopy.addEventListener('click', copyPrompt);
  btnDownload.addEventListener('click', downloadPrompt);
  btnCopyMobile.addEventListener('click', copyPrompt);
  btnDownloadMobile.addEventListener('click', downloadPrompt);

  // Mobile preview
  btnMobilePreview.addEventListener('click', () => toggleMobilePreview(true));
  btnClosePreview.addEventListener('click', () => toggleMobilePreview(false));

  // Debounced input listeners for all form fields
  const debouncedSync = debounce(() => {
    syncInputsToState();
    updatePreview();
  }, 300);

  for (const mapping of FIELD_MAPPINGS) {
    const el = document.getElementById(mapping.id);
    if (el) el.addEventListener('input', debouncedSync);
  }
}

// ── Step navigation ────────────────────────────────────────────────

function goToStep(n: number) {
  if (n < 0 || n >= STEPS.length) return;
  state.currentStep = n;

  // Show/hide panels
  stepPanels.forEach((panel) => {
    const panelStep = parseInt(panel.dataset.step ?? '-1', 10);
    panel.classList.toggle('hidden', panelStep !== n);
  });

  // Update desktop sidebar
  stepNavBtns.forEach((btn) => {
    const btnStep = parseInt(btn.dataset.goto ?? '-1', 10);
    const isActive = btnStep === n;
    const hasContent = stepHasContent(state, btnStep);
    btn.classList.toggle('active', isActive);

    const numSpan = btn.querySelector('.step-num');
    const labelSpan = btn.querySelector('span:last-child');
    if (numSpan) {
      if (isActive) {
        numSpan.className =
          'step-num flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-accent bg-accent/10 text-xs font-medium text-accent';
      } else if (hasContent) {
        numSpan.className =
          'step-num flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-accent/50 text-xs font-medium text-accent/70';
      } else {
        numSpan.className =
          'step-num flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-border/70 text-xs font-medium text-muted';
      }
    }
    if (labelSpan) {
      labelSpan.className = isActive
        ? 'text-text'
        : hasContent
          ? 'text-text/70'
          : 'text-muted';
    }
  });

  // Update mobile pills
  stepPills.forEach((btn) => {
    const btnStep = parseInt(btn.dataset.goto ?? '-1', 10);
    const isActive = btnStep === n;
    if (isActive) {
      btn.className =
        'step-pill active shrink-0 rounded-full border border-accent bg-accent/10 px-3 py-1.5 text-xs font-medium text-accent transition';
    } else {
      btn.className =
        'step-pill shrink-0 rounded-full border border-border/70 px-3 py-1.5 text-xs font-medium text-muted transition';
    }
  });

  // Update Prev/Next
  btnPrev.disabled = n === 0;
  if (n === STEPS.length - 1) {
    btnNext.textContent = 'Done';
  } else {
    btnNext.textContent = 'Next';
  }
}

function navigateStep(delta: number) {
  const next = state.currentStep + delta;
  if (next < 0) return;
  if (next >= STEPS.length) {
    // On "Done", sync and update
    syncInputsToState();
    updatePreview();
    return;
  }
  syncInputsToState();
  goToStep(next);
  updatePreview();
}

// ── State sync ─────────────────────────────────────────────────────

function syncInputsToState() {
  for (const mapping of FIELD_MAPPINGS) {
    const el = document.getElementById(mapping.id) as
      | HTMLInputElement
      | HTMLTextAreaElement
      | null;
    if (el) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (state[mapping.step] as any)[mapping.field] = el.value;
    }
  }

  // Sync examples from DOM
  syncExamplesFromDom();
}

function syncExamplesFromDom() {
  const cards = examplesList.querySelectorAll('[data-example-id]');
  state.examples.examples = Array.from(cards).map((card) => {
    const id = (card as HTMLElement).dataset.exampleId ?? '';
    const userMsg =
      (card.querySelector('.example-user') as HTMLTextAreaElement)?.value ?? '';
    const assistantMsg =
      (card.querySelector('.example-assistant') as HTMLTextAreaElement)?.value ?? '';
    return { id, userMessage: userMsg, assistantResponse: assistantMsg };
  });
}

function populateAllInputs() {
  for (const mapping of FIELD_MAPPINGS) {
    const el = document.getElementById(mapping.id) as
      | HTMLInputElement
      | HTMLTextAreaElement
      | null;
    if (el) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      el.value = (state[mapping.step] as any)[mapping.field] ?? '';
    }
  }

  // Populate examples
  renderExamplesList();

  // Go to step 0 to show first step
  goToStep(state.currentStep);
}

// ── Preview ────────────────────────────────────────────────────────

function updatePreview() {
  const prompt = assemblePrompt(state);
  const score = calculateHardeningScore(state.hardening.defenses);
  const label = getScoreLabel(score);

  // Desktop preview
  if (prompt) {
    previewText.textContent = prompt;
  } else {
    previewText.innerHTML =
      '<span class="text-muted">Your system prompt will appear here as you fill in the steps...</span>';
  }

  // Desktop score
  if (stepHasContent(state, 6) || score > 0) {
    previewScore.classList.remove('hidden');
    previewScoreLabel.textContent = `${label} (${score}%)`;
    previewScoreBar.style.width = `${score}%`;
  } else {
    previewScore.classList.add('hidden');
  }

  // Mobile preview
  if (prompt) {
    mobilePreviewText.textContent = prompt;
  } else {
    mobilePreviewText.innerHTML =
      '<span class="text-muted">Your system prompt will appear here...</span>';
  }

  // Mobile score
  if (stepHasContent(state, 6) || score > 0) {
    mobilePreviewScore.classList.remove('hidden');
    mobilePreviewScoreLabel.textContent = `${label} (${score}%)`;
    mobilePreviewScoreBar.style.width = `${score}%`;
  } else {
    mobilePreviewScore.classList.add('hidden');
  }

  // Hardening step score
  hardeningScoreLabel.textContent = `${label} (${score}%)`;
  hardeningScoreBar.style.width = `${score}%`;

  // Update score bar colors
  const scoreColor =
    score >= 90
      ? 'bg-emerald-500'
      : score >= 70
        ? 'bg-accent'
        : score >= 40
          ? 'bg-amber-500'
          : 'bg-rose-500';
  for (const bar of [hardeningScoreBar, previewScoreBar, mobilePreviewScoreBar]) {
    bar.className = bar.className.replace(
      /bg-(?:emerald-500|accent|amber-500|rose-500)/,
      scoreColor
    );
  }
}

// ── Defense toggles ────────────────────────────────────────────────

function toggleDefense(defenseId: string) {
  state.hardening.defenses[defenseId] = !state.hardening.defenses[defenseId];
  updateDefenseToggles();
  updatePreview();
}

function updateDefenseToggles() {
  defenseBtns.forEach((btn) => {
    const defenseId = btn.dataset.defenseId;
    if (!defenseId) return;
    const isOn = !!state.hardening.defenses[defenseId];
    btn.textContent = isOn ? 'On' : 'Off';
    btn.classList.toggle('enabled', isOn);

    // Update parent card styling
    const card = btn.closest('.defense-card');
    if (card) card.classList.toggle('enabled', isOn);
  });
}

// ── Examples ───────────────────────────────────────────────────────

function addExample() {
  exampleCounter++;
  const id = `ex-${exampleCounter}`;
  state.examples.examples.push({ id, userMessage: '', assistantResponse: '' });
  renderExamplesList();
}

function removeExample(id: string) {
  state.examples.examples = state.examples.examples.filter((e) => e.id !== id);
  renderExamplesList();
  updatePreview();
}

function renderExamplesList() {
  examplesList.innerHTML = '';

  const debouncedUpdate = debounce(() => {
    syncExamplesFromDom();
    updatePreview();
  }, 300);

  state.examples.examples.forEach((example, index) => {
    const card = document.createElement('div');
    card.className = 'rounded-lg border border-border/70 bg-surface/80 p-4';
    card.dataset.exampleId = example.id;
    card.innerHTML = `
      <div class="flex items-center justify-between mb-3">
        <span class="text-xs uppercase tracking-[0.3em] text-muted">Example ${index + 1}</span>
        <button type="button" class="btn-remove-example rounded-full border border-border/70 px-2 py-0.5 text-xs text-muted transition hover:border-rose-500 hover:text-rose-500" data-remove-id="${escapeAttr(example.id)}">Remove</button>
      </div>
      <div class="grid gap-3">
        <div>
          <label class="text-sm font-medium text-text">User message</label>
          <textarea class="example-user mt-1 w-full rounded-lg border border-border/70 bg-input-bg px-4 py-2 text-sm text-text focus:border-accent focus:outline-none" rows="2" placeholder="What the user says...">${escapeHtml(example.userMessage)}</textarea>
        </div>
        <div>
          <label class="text-sm font-medium text-text">Assistant response</label>
          <textarea class="example-assistant mt-1 w-full rounded-lg border border-border/70 bg-input-bg px-4 py-2 text-sm text-text focus:border-accent focus:outline-none" rows="2" placeholder="How the assistant should respond...">${escapeHtml(example.assistantResponse)}</textarea>
        </div>
      </div>
    `;

    // Remove button
    const removeBtn = card.querySelector('.btn-remove-example') as HTMLButtonElement;
    removeBtn.addEventListener('click', () => removeExample(example.id));

    // Input listeners
    const textareas = card.querySelectorAll('textarea');
    textareas.forEach((ta) => ta.addEventListener('input', debouncedUpdate));

    examplesList.appendChild(card);
  });
}

// ── Copy / Download ────────────────────────────────────────────────

function copyPrompt() {
  syncInputsToState();
  const prompt = assemblePrompt(state);
  if (!prompt) return;
  copyTextToClipboard(prompt)
    .then(() => {
      flashCopyButtons('Copied!');
    })
    .catch(() => {
      flashCopyButtons('Copy failed');
    });
}

function downloadPrompt() {
  syncInputsToState();
  const prompt = assemblePrompt(state);
  if (!prompt) return;
  const blob = new Blob([prompt], { type: 'text/markdown' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'system-prompt.md';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ── Mobile preview ─────────────────────────────────────────────────

function toggleMobilePreview(show: boolean) {
  if (show) {
    syncInputsToState();
    updatePreview();
    mobilePreviewOverlay.classList.remove('hidden');
    mobilePreviewOverlay.classList.add('flex');
    document.body.style.overflow = 'hidden';
  } else {
    mobilePreviewOverlay.classList.add('hidden');
    mobilePreviewOverlay.classList.remove('flex');
    document.body.style.overflow = '';
  }
}

// ── Utilities ──────────────────────────────────────────────────────

function escapeHtml(str: string): string {
  const div = document.createElement('div');
  div.textContent = str;
  return div.innerHTML;
}

function escapeAttr(str: string): string {
  return str.replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

function flashCopyButtons(message: string) {
  const buttons = [btnCopy, btnCopyMobile];
  const originalTexts = buttons.map((btn) => btn.textContent ?? 'Copy');
  buttons.forEach((btn) => {
    btn.textContent = message;
  });
  setTimeout(() => {
    buttons.forEach((btn, index) => {
      btn.textContent = originalTexts[index];
    });
  }, 1500);
}

function copyTextToClipboard(value: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    return navigator.clipboard.writeText(value);
  }

  const temp = document.createElement('textarea');
  temp.value = value;
  temp.style.position = 'fixed';
  temp.style.opacity = '0';
  document.body.appendChild(temp);
  temp.focus();
  temp.select();

  try {
    const copied = document.execCommand('copy');
    if (!copied) {
      return Promise.reject(new Error('Clipboard copy failed.'));
    }
    return Promise.resolve();
  } finally {
    temp.remove();
  }
}

function debounce<T extends (...args: unknown[]) => void>(fn: T, delay: number): T {
  let timeout: ReturnType<typeof setTimeout>;
  return ((...args: unknown[]) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => fn(...args), delay);
  }) as T;
}

// ── Bootstrap ──────────────────────────────────────────────────────

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
